// @ts-check
import { gql, useQuery } from '@apollo/client';
import { Trans, t, plural } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import React from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Flex,
  Table,
  TableBody,
  Text,
  useConfirmation,
} from '~/design-system';
import Notification from '../../Notification';
import DefaultTableHeader from '../DefaultTableHeader';
import ImportsOverviewTableBodySkeleton from '../ImportsOverviewTableBodySkeleton';
import useDeleteImport from '../useDeleteImport';
import ImportInProgressRow from './ImportInProgressRow';

export default function ImportsInProgress({ refetch }) {
  const { i18n } = useLingui();
  const {
    loading,
    inProgressImports,
    deleteImport,
    deleteImports,
    confirm,
    notification,
    setNotification,
  } = useImportsInProgress({ refetch });

  const canBulkDelete = !loading && inProgressImports.length > 1;
  return (
    <React.Fragment>
      <Box sx={{ width: '100%', margin: '0 auto', paddingBottom: 7 }}>
        {!loading && inProgressImports.length === 0 ? (
          <Text sx={{ marginTop: 4 }}>
            You don't have any imports currently in progress.
          </Text>
        ) : (
          <Table
            sx={{
              border: '1px solid',
              borderColor: 'brand.lightGray',
              width: '100%',
              textAlign: 'left',
            }}
          >
            <DefaultTableHeader />
            {loading ? (
              <ImportsOverviewTableBodySkeleton />
            ) : (
              <TableBody>
                {inProgressImports?.map((oneImport, i) => (
                  <ImportInProgressRow
                    key={i}
                    oneImport={oneImport}
                    onDelete={(oneImport) => {
                      confirm({
                        message: `Do you really want to remove ${oneImport.name}?`,
                        onConfirm: async () => {
                          const success = await deleteImport(
                            oneImport.importId
                          );
                          if (!success) {
                            setNotification({
                              message: 'There was an error, please try again!',
                            });
                          }
                        },
                      });
                    }}
                  />
                ))}
              </TableBody>
            )}
          </Table>
        )}

        {canBulkDelete && (
          <Flex sx={{ justifyContent: 'flex-end', marginTop: 3 }}>
            <Button
              variant="text"
              onClick={() => {
                const count = inProgressImports.length;
                const itemsPhrase = plural(count, {
                  one: '# import in progress',
                  other: '# imports in progress',
                });
                confirm({
                  message: i18n._(
                    t`Delete all ${itemsPhrase}? This can't be undone.`
                  ),
                  onConfirm: async () => {
                    const { successCount, failureCount } = await deleteImports(
                      inProgressImports.map((imp) => imp.importId)
                    );
                    if (failureCount > 0) {
                      setNotification({
                        message: i18n._(
                          t`Removed ${successCount} of ${count} — ${failureCount} could not be deleted.`
                        ),
                      });
                    } else {
                      const removed = plural(successCount, {
                        one: '# in-progress import',
                        other: '# in-progress imports',
                      });
                      setNotification({
                        success: true,
                        message: i18n._(t`Removed ${removed}.`),
                      });
                    }
                  },
                });
              }}
              sx={{ color: '#dc2626' }}
            >
              <Trans>Delete all in-progress imports</Trans>
            </Button>
          </Flex>
        )}
      </Box>

      {notification && (
        <Notification
          onClose={() => setNotification(undefined)}
          message={notification.message}
          // aLinkData={notification.aLinkData}
          variant={notification.success ? 'success' : 'error'}
        />
      )}
    </React.Fragment>
  );
}

function useImportsInProgress({ refetch: parentRefetch }) {
  const { workspaceId } = useParams();
  const { data, loading, refetch } = useQuery(query, {
    variables: { workspaceId },
  });
  const { deleteImport, deleteImports } = useDeleteImport({
    refetch: async () => {
      await parentRefetch();
      await refetch();
    },
  });
  const confirm = useConfirmation();
  /** @type {[import('../../Notification').Notification | undefined, React.Dispatch<React.SetStateAction<import('../../Notification').Notification | undefined>>]} */
  const [notification, setNotification] = React.useState();
  const inProgressImports = (
    data?.workspace?.dataImports?.nodes?.filter(Boolean) || []
  ).sort((a, b) => {
    return new Date(b?.createdAt) - new Date(a?.createdAt);
  });
  return {
    loading,
    inProgressImports,
    deleteImport,
    deleteImports,
    confirm,
    notification,
    setNotification,
  };
}

const query = gql`
  query ImportsInProgressPage($workspaceId: ID!) {
    workspace(workspaceId: $workspaceId) {
      workspaceId
      dataImports(filter: { state: { in: ["JOB_IN_PROGRESS", "STARTED"] } }) {
        nodes {
          ...ImportOverview
        }
      }
    }
  }

  fragment ImportOverview on DataImport {
    importId
    name
    creator {
      fullName
      email
    }
    importOptions
    source {
      ... on DataImportSourceTabular {
        url
      }
    }
    state {
      state
    }
    type
    createdAt
  }
`;
